/**
 * @module features/search/universal-search
 * @description Universal search functionality for cross-entity search
 * Handles searching across all entity types (projects, opportunities, leads, contacts)
 */

import { universalSearch } from '../../core/api/entity-api.js';
import { ENTITY_TYPES } from '../entity/entity-types.js';
import { navigateToEntity } from '../../core/router/url-router.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { formatFileSize, formatDate } from '../../utils/formatters.js';
import { logger } from '../../utils/logger.js';
import { showWarning, showError } from '../../ui/components/error-display.js';

// Track if universal search has been initialized
let universalSearchInitialized = false;

/**
 * Perform universal search across all entity types
 * @param {string} searchTerm - Search term (minimum 3 characters)
 * @param {Object} _user - Firebase user object
 * @returns {Promise<Object>} Object with results grouped by entity type
 * @throws {Error} If search term is too short or search fails
 */
export async function performUniversalSearch(searchTerm, _user) {
    if (!searchTerm || searchTerm.length < 3) {
        throw new Error('Search term must be at least 3 characters');
    }

    try {
        // Use the entity-api's universalSearch function
        // which already handles parallel searches and error handling
        return await universalSearch(searchTerm);
    } catch (error) {
        logger.error('❌ Error performing universal search:', error);
        throw error;
    }
}

/**
 * Display universal search results grouped by entity type
 * @param {Object} searchData - Search results data with { results, totalCount, searchTerm }
 * @param {Object} user - Firebase user object
 */
export function displayUniversalSearchResults(searchData, user) {
    logger.debug('📋 Displaying universal search results:', searchData);

    const resultsContainer = document.getElementById('unified-results-container');
    const resultsSection = document.getElementById('unified-search-results');

    if (!resultsContainer) {
        logger.error('❌ Results container not found');
        return;
    }

    if (!resultsSection) {
        logger.error('❌ Results section not found');
        return;
    }

    logger.debug('✅ Found results container and section');

    // Clear previous results
    resultsContainer.innerHTML = '';

    // Show results section
    resultsSection.classList.remove('hidden');
    logger.debug('✅ Results section shown');

    // Check if we have any results
    if (searchData.totalCount === 0) {
        logger.debug('ℹ️ No results found');
        resultsContainer.innerHTML = `
            <div class="no-results">
                <div class="no-results-icon">🔍</div>
                <p>No results found for "<strong>${escapeHtml(searchData.searchTerm)}</strong>"</p>
                <p class="no-results-hint">Try a different search term or browse by type below</p>
            </div>
        `;
        return;
    }

    logger.debug(`✅ Displaying ${searchData.totalCount} results`);

    // Display results grouped by entity type
    Object.entries(searchData.results).forEach(([entityType, items]) => {
        if (items.length === 0) return;

        logger.debug(`  → Adding ${items.length} ${entityType} results`);

        const config = ENTITY_TYPES[entityType];
        const groupDiv = document.createElement('div');
        groupDiv.className = 'result-group';

        // Group header
        const headerDiv = document.createElement('div');
        headerDiv.className = 'result-group-header';
        headerDiv.innerHTML = `
            <span class="result-group-icon">${config.icon}</span>
            <span class="result-group-title">${config.label}s</span>
            <span class="result-group-count">${items.length}</span>
        `;
        groupDiv.appendChild(headerDiv);

        // Results list
        const listDiv = document.createElement('div');
        listDiv.className = 'result-list';

        items.forEach(item => {
            const resultItem = createResultItem(item, entityType, config, user);
            listDiv.appendChild(resultItem);
        });

        groupDiv.appendChild(listDiv);
        resultsContainer.appendChild(groupDiv);
    });
}

/**
 * Create a single result item element
 * @param {Object} item - Entity metadata
 * @param {string} entityType - Type of entity
 * @param {Object} config - Entity type configuration
 * @param {Object} _user - Firebase user object
 * @returns {HTMLElement} Result item element
 */
export function createResultItem(item, entityType, config, _user) {
    const itemDiv = document.createElement('div');
    itemDiv.className = 'result-item';

    // Main info
    const mainDiv = document.createElement('div');
    mainDiv.className = 'result-item-main';

    const nameDiv = document.createElement('div');
    nameDiv.className = 'result-item-name';
    nameDiv.textContent = item.entityName || item.name || 'Unnamed';

    const metaDiv = document.createElement('div');
    metaDiv.className = 'result-item-meta';

    // Add metadata
    const metaParts = [];
    if (item.fileCount !== undefined) {
        metaParts.push(`${item.fileCount} file${item.fileCount !== 1 ? 's' : ''}`);
    }
    if (item.totalSize !== undefined) {
        metaParts.push(formatFileSize(item.totalSize));
    }
    if (config.hasSudbId && item.sudbId) {
        metaParts.push(`SUDB: ${item.sudbId}`);
    }
    if (item.created) {
        metaParts.push(formatDate(item.created));
    }

    metaDiv.textContent = metaParts.join(' • ');

    mainDiv.appendChild(nameDiv);
    mainDiv.appendChild(metaDiv);
    itemDiv.appendChild(mainDiv);

    // Linked entities section
    if (item.linkedEntities && item.linkedEntities.length > 0) {
        const linkedDiv = document.createElement('div');
        linkedDiv.className = 'result-item-linked';

        const linkedLabel = document.createElement('span');
        linkedLabel.className = 'linked-label';
        linkedLabel.textContent = 'Linked: ';
        linkedDiv.appendChild(linkedLabel);

        item.linkedEntities.forEach((linked, index) => {
            if (index > 0) {
                linkedDiv.appendChild(document.createTextNode(', '));
            }

            const linkedLink = document.createElement('a');
            linkedLink.href = '#';
            linkedLink.className = 'linked-entity';
            linkedLink.textContent = `${ENTITY_TYPES[linked.linkedEntityType]?.icon || '📎'} ${linked.linkedEntityEntityName}`;
            linkedLink.title = `View ${ENTITY_TYPES[linked.linkedEntityType]?.label || 'entity'}`;

            linkedLink.addEventListener('click', (e) => {
                e.preventDefault();
                navigateToEntity(linked.linkedEntityType, linked.linkedEntityId);
            });

            linkedDiv.appendChild(linkedLink);
        });

        itemDiv.appendChild(linkedDiv);
    }

    // Actions
    const actionsDiv = document.createElement('div');
    actionsDiv.className = 'result-item-actions';

    const viewBtn = document.createElement('button');
    viewBtn.className = 'btn btn-primary btn-small';
    viewBtn.textContent = 'View Files';
    viewBtn.addEventListener('click', () => {
        navigateToEntity(entityType, item.entityId || item.id);
    });

    const crmBtn = document.createElement('button');
    crmBtn.className = 'btn btn-secondary btn-small';
    crmBtn.textContent = 'Open in Insightly';
    crmBtn.addEventListener('click', () => {
        window.open(config.insightlyUrl + (item.entityId || item.id), '_blank');
    });

    actionsDiv.appendChild(viewBtn);
    actionsDiv.appendChild(crmBtn);
    itemDiv.appendChild(actionsDiv);

    return itemDiv;
}

/**
 * Initialize universal search functionality
 * Sets up event listeners and UI state for the universal search interface
 * @param {Object} user - Firebase user object
 */
export function initializeUniversalSearch(user) {
    const searchInput = document.getElementById('unified-search-input');
    const searchBtn = document.getElementById('unified-search-btn');
    const clearBtn = document.getElementById('clear-search-btn');
    const browseToggle = document.getElementById('browse-toggle-btn');
    const browseContent = document.getElementById('browse-content');

    if (!searchInput || !searchBtn) {
        logger.warn('Universal search elements not found');
        return;
    }

    // Only set up event listeners once
    if (!universalSearchInitialized) {
        // Search button click handler
        searchBtn.addEventListener('click', async () => {
            const searchTerm = searchInput.value.trim();

            if (searchTerm.length < 3) {
                showWarning('Please enter at least 3 characters to search');
                return;
            }

            try {
                // Show loading state
                searchBtn.disabled = true;
                searchBtn.textContent = '⏳ Searching...';

                // Perform search
                const results = await performUniversalSearch(searchTerm, user);

                // Display results
                displayUniversalSearchResults(results, user);

            } catch (error) {
                logger.error('Search error:', error);
                showError('Search failed. Please try again.');
            } finally {
                // Restore button state
                searchBtn.disabled = false;
                searchBtn.textContent = '🔍 Search All';
            }
        });

        // Enter key handler
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                searchBtn.click();
            }
        });

        // Clear button handler
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                searchInput.value = '';
                document.getElementById('unified-search-results').classList.add('hidden');
                searchInput.focus();
            });
        }

        // Browse toggle handler
        if (browseToggle && browseContent) {
            browseToggle.addEventListener('click', () => {
                const isCollapsed = browseContent.classList.contains('collapsed');
                if (isCollapsed) {
                    browseContent.classList.remove('collapsed');
                    browseToggle.querySelector('.browse-toggle-icon').textContent = '▼';
                } else {
                    browseContent.classList.add('collapsed');
                    browseToggle.querySelector('.browse-toggle-icon').textContent = '▶';
                }
            });
        }

        universalSearchInitialized = true;
    }

    // Always reset browse state (collapsed and enabled) when showing this view
    if (browseToggle && browseContent) {
        browseToggle.disabled = false;
        browseContent.classList.add('collapsed');
        const toggleIcon = browseToggle.querySelector('.browse-toggle-icon');
        if (toggleIcon) {
            toggleIcon.textContent = '▶';
        }
    }

    // Focus search input
    searchInput.focus();
}

/**
 * Reset universal search state
 * Clears search input and hides results
 */
export function resetUniversalSearch() {
    const searchInput = document.getElementById('unified-search-input');
    const resultsSection = document.getElementById('unified-search-results');

    if (searchInput) {
        searchInput.value = '';
    }

    if (resultsSection) {
        resultsSection.classList.add('hidden');
    }
}

/**
 * Check if universal search is initialized
 * @returns {boolean} True if initialized
 */
export function isUniversalSearchInitialized() {
    return universalSearchInitialized;
}
